/**
 * autofill.js
 * ===========
 * Centralized "intelligent" auto-fill logic for quotes. Used by:
 *   - EstimateBuilder.runAIEstimate (after AI Vision completes)
 *   - LeadDetail.instantQuote (the "one-click everything" flow)
 *
 * Rules of engagement:
 *   • This is the "magic" the app sells. Defaults should reflect reality:
 *     asphalt-shingle measurements get shingle materials; metal gets metal;
 *     tile gets tile. Pricing comes from the org's Pricing Profile (not generic
 *     fallbacks) so each roofer's numbers reflect THEIR cost basis.
 *   • Never overwrite a field the contractor has already manually edited
 *     (heuristic: only swap if the current value matches a known stock default
 *     OR is empty).
 *   • Returns the patch to persist via PATCH /api/quotes/{id} — caller saves.
 */

// Industry-standard material templates per detected roof material. The brand
// names are the most-used in their category (GAF for asphalt is ~30% market
// share; Standing Seam for metal; Eagle/Boral for tile). Contractors can swap
// brand-by-brand inline in EstimateBuilder, but these are sensible defaults
// that make the very first preview look like a real quote, not a stub.
const MATERIAL_TEMPLATES = {
    asphalt_shingle: [
        { category: 'Shingles',         brand_type_grade: 'GAF Timberline HDZ (Architectural)' },
        { category: 'Underlayment',     brand_type_grade: 'GAF FeltBuster Synthetic' },
        { category: 'Ice & Water Shield', brand_type_grade: 'GAF Weather Watch (eaves + valleys)' },
        { category: 'Hip & Ridge Cap',  brand_type_grade: 'GAF TimberTex' },
        { category: 'Flashing',         brand_type_grade: 'New aluminum drip edge + pipe boots' },
    ],
    metal: [
        { category: 'Metal Panels',     brand_type_grade: 'Standing seam 24-gauge, painted Kynar finish' },
        { category: 'Underlayment',     brand_type_grade: 'Synthetic high-temp underlayment (250\u00B0F rated)' },
        { category: 'Trim & Flashing',  brand_type_grade: 'Matching color-coordinated metal trim, ridge cap, eave trim' },
        { category: 'Closure Strips',   brand_type_grade: 'Foam outside-closure strips at eaves + ridges' },
        { category: 'Fasteners',        brand_type_grade: 'EPDM-gasketed screws or concealed clip system' },
    ],
    tile: [
        { category: 'Tile',             brand_type_grade: 'Eagle Concrete Tile (or owner-selected pattern)' },
        { category: 'Underlayment',     brand_type_grade: 'High-temp 40# felt + ice & water at valleys' },
        { category: 'Battens',          brand_type_grade: '1x2 wood battens, treated' },
        { category: 'Flashing',         brand_type_grade: 'Lead pipe flashings + step flashing at walls' },
        { category: 'Ridge & Hip',      brand_type_grade: 'Mortar-set or mechanically-fastened ridge tile' },
    ],
    flat_membrane: [
        { category: 'TPO Membrane',     brand_type_grade: 'GAF EverGuard TPO 60-mil, fully adhered' },
        { category: 'Insulation',       brand_type_grade: 'Polyiso 2.5" R-13.6 (per roof spec)' },
        { category: 'Cover Board',      brand_type_grade: 'DensDeck Prime 1/2"' },
        { category: 'Edge Metal',       brand_type_grade: 'Coated steel edge metal + termination bar' },
        { category: 'Fasteners',        brand_type_grade: 'Manufacturer-approved plates + screws' },
    ],
    wood_shake: [
        { category: 'Cedar Shakes',     brand_type_grade: 'Western red cedar #1 grade, 24" medium' },
        { category: 'Underlayment',     brand_type_grade: 'Cedar Breather + 30# felt interlayment' },
        { category: 'Flashing',         brand_type_grade: 'Copper or galvanized step flashing' },
        { category: 'Ridge & Hip',      brand_type_grade: 'Pre-fab cedar ridge cap' },
    ],
};

// Fallback used when AI returns 'unknown' or no measurement — keeps the current
// GAF-asphalt template since asphalt is ~75% of US residential roofs.
const DEFAULT_TEMPLATE = MATERIAL_TEMPLATES.asphalt_shingle;


/**
 * Build a materials + costs patch from AI Vision output + the org's Pricing Profile.
 *
 * @param {object} args
 * @param {object} args.measurements  Roof measurements from AI Vision
 *                                    { roof_sqft, material_guess, pitch, complexity }
 * @param {object} args.pricingProfile Per-org Pricing Profile (already merged with defaults)
 * @param {number} args.stories        Number of building stories (drives labor multiplier)
 * @returns {{ materials: Array, costs: object }} ready to send to PATCH /api/quotes/{id}
 */
export function buildAutofillFromAI({ measurements, pricingProfile, stories = 1 }) {
    const sqft = Number(measurements?.roof_sqft || 0);
    if (!sqft) {
        return { materials: null, costs: null };
    }

    // Pick the material template based on what AI actually saw.
    // 'unknown' falls back to asphalt (most common).
    const guess = (measurements?.material_guess || 'unknown').toLowerCase();
    const template = MATERIAL_TEMPLATES[guess] || DEFAULT_TEMPLATE;

    const squares = Math.ceil(sqft / 100);
    const prof = pricingProfile || {};
    const wastePct = Number(prof.waste_pct ?? 12);
    const marginPct = Number(prof.margin_pct ?? 25);
    const adjSquares = Math.ceil(squares * (1 + wastePct / 100));
    const storyKey = stories >= 3 ? '3' : String(stories);
    const storyMult = Number(
        prof.story_multipliers?.[storyKey] ?? (storyKey === '2' ? 1.15 : storyKey === '3' ? 1.30 : 1.0)
    );
    const iceRolls = Math.max(2, Math.ceil(squares / 6));

    // Auto-fill quantities into the template categories where it makes sense.
    // (Brand stays as the template spec; only quantity reflects this property.)
    const materials = template.map(item => {
        const cat = (item.category || '').toLowerCase();
        let qty = '';
        if (cat.includes('shingle') || cat.includes('tile') || cat.includes('panel') || cat.includes('shake') || cat.includes('membrane')) {
            qty = `${adjSquares} sq`;
        } else if (cat.includes('underlayment')) {
            qty = `${adjSquares} sq`;
        } else if (cat.includes('ice')) {
            qty = `${iceRolls} rolls`;
        } else if (cat.includes('ridge') || cat.includes('hip')) {
            // Rough rule: ridge length ~ sqrt(sqft) for square-ish footprints
            const ridgeLf = Math.ceil(Math.sqrt(sqft) * 1.2);
            qty = `${ridgeLf} lf`;
        } else if (cat.includes('flashing') || cat.includes('trim') || cat.includes('closure') || cat.includes('edge')) {
            const lf = Math.ceil(Math.sqrt(sqft) * 4);  // perimeter approximation
            qty = `${lf} lf`;
        }
        return { ...item, quantity: qty };
    });

    // Cost math — per-square × pricing-profile × story multiplier
    // Materials per-square varies by template; we fall back to shingle pricing
    // for unknown templates. Eventually each template could carry its own
    // per-square cost in the Pricing Profile (e.g. metal_per_square).
    const matBase = adjSquares * Number(prof.shingle_per_square ?? 95)
        + adjSquares * Number(prof.underlayment_per_square ?? 22)
        + iceRolls * Number(prof.ice_water_per_roll ?? 85);
    const labor = squares * Number(prof.labor_per_square ?? 175) * storyMult;
    const disposal = Number(prof.dump_fee ?? 450);
    const permit = Number(prof.permit_fee ?? 250);
    const base = matBase + labor + disposal + permit;
    // Gross up so the post-margin total hits the contractor's profit target
    const target = base / Math.max(1 - marginPct / 100, 0.01);
    const other = Math.max(0, target - base);

    const costs = {
        materials: Math.round(matBase),
        labor: Math.round(labor),
        disposal: Math.round(disposal),
        permit: Math.round(permit),
        other: Math.round(other),
    };

    return {
        materials,
        costs,
        // Diagnostic info the UI can use to show "what we did"
        _summary: {
            sqft,
            squares,
            adjSquares,
            wastePct,
            storyMult,
            template: guess in MATERIAL_TEMPLATES ? guess : 'asphalt_shingle_fallback',
            marginPct,
        },
    };
}
